import _ from 'lodash'
import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import {
  DataGridPremium,
  DataGridPremiumProps,
  GridLoadingOverlayProps,
  GridRowParams,
  GridToolbar,
} from '@mui/x-data-grid-premium'
import { useWindowSize } from 'react-use'
import { SORTING_ORDER } from './constants'

const slots = {
  toolbar: GridToolbar,
}

export const loadingOverlay = {
  variant: 'skeleton',
  noRowsVariant: 'linear-progress',
} as GridLoadingOverlayProps

export const loadingMoreOverlay = {
  variant: 'linear-progress',
  noRowsVariant: 'linear-progress',
} as GridLoadingOverlayProps

export const AGGREGATE_DATA_ROW_ID = 'auto-generated-group-footer-root'

type DataGridProps = DataGridPremiumProps & {
  height: number
  activeRowId?: string
  onMouseEnter?: ({ id }: { id?: string }) => void
  onMouseLeave?: () => void
}
// ! Improve the performance of the DataGrid using the recommendations from https://mui.com/x/react-data-grid/performance/
const DataGrid = ({
  slotProps: propsSlotProps,
  height,
  getRowId,
  activeRowId,
  onMouseEnter,
  onMouseLeave,
  ...props
}: DataGridProps) => {
  const getRowClassName = useCallback(
    (params: GridRowParams) => {
      return getRowId?.(params.row) === activeRowId ? 'Mui-selected' : ''
    },
    [activeRowId, getRowId]
  )

  const slotProps = useMemo(() => {
    return _.merge(
      {
        loadingOverlay,
        toolbar: {
          csvOptions: { allColumns: true },
        },
        row: {
          onMouseEnter: (event: React.MouseEvent<HTMLDivElement>) => {
            const rowId = event.currentTarget.dataset.id
            return onMouseEnter?.({
              id: rowId === AGGREGATE_DATA_ROW_ID ? undefined : rowId,
            })
          },
          onMouseLeave,
        },
      },
      propsSlotProps
    )
  }, [onMouseEnter, onMouseLeave, propsSlotProps])

  return (
    <div
      style={{
        height,
        width: '100%',
      }}
    >
      <DataGridPremium
        sortingOrder={SORTING_ORDER}
        disableRowSelectionOnClick
        slots={slots}
        slotProps={slotProps}
        autoPageSize
        hideFooter
        getRowId={getRowId}
        getRowClassName={getRowClassName}
        {...props}
      />
    </div>
  )
}

const DataGridContainer = (passProps: Omit<DataGridProps, 'height'>) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const { height: windowHeight } = useWindowSize()

  const [containerHeight, setContainerHeight] = useState(windowHeight)

  const updateHeight = useCallback(() => {
    if (containerRef.current) {
      setContainerHeight(containerRef.current.offsetHeight)
    }
  }, [])

  useEffect(() => {
    const resizeObserver = new ResizeObserver(updateHeight)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }
    return () => resizeObserver.disconnect()
  }, [updateHeight])

  useEffect(() => {
    updateHeight()
  }, [windowHeight, updateHeight])

  return (
    <div ref={containerRef} style={{ position: 'relative', height: '100%' }}>
      <DataGrid height={containerHeight} {...passProps} />
    </div>
  )
}

export default DataGridContainer
